'use client';
import { FC, forwardRef, InputHTMLAttributes, useState } from 'react';

import cn from 'classnames';
import Image from 'next/image';
import { FieldError } from 'react-hook-form';

import PlusCircleIcon from '@heroicons/react/20/solid/PlusCircleIcon';
import XCircleIcon from '@heroicons/react/24/outline/XCircleIcon';

import LoadingDots from './loading/loading-dots.components';

interface ImageInputProps extends InputHTMLAttributes<HTMLInputElement> {
  name: string;
  label?: string;
  helpText?: string;
  error?: FieldError;
  url?: string;
  handleUpload?: () => void;
  handleRemove?: () => void;
  lang?: string;
  fileName?: string;
}

const ImageInput: FC<ImageInputProps> = forwardRef<
  HTMLInputElement,
  ImageInputProps
>(
  (
    {
      name,
      label,
      helpText,
      error,
      className,
      url,
      handleUpload,
      handleRemove,
      lang,
      fileName,
      ...rest
    },
    ref,
  ) => {
    const [loading, setLoading] = useState(false);

    const chooseFileText = lang === 'tr' ? 'Dosya Seç' : 'Choose file';
    const noFileChosenText =
      lang === 'tr' ? 'Dosya seçilmedi' : 'No file chosen';
    const uploadingText = lang === 'tr' ? 'Yükleniyor...' : 'Uploading...';

    return (
      <div className={cn(className)}>
        {label && (
          <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
            {label}
          </label>
        )}

        <div className="relative flex w-full items-center rounded-lg border border-gray-300 bg-white p-1.5 shadow-sm">
          <label
            htmlFor={name}
            className={cn(
              'inline-flex min-w-[128px] cursor-pointer items-center justify-center rounded-md border border-[rgba(0,0,0,0.1)] px-3 py-1.5 shadow-sm sm:text-sm',
              {
                ['bg-red-300 text-red-900']: !!error,
                ['bg-cyan-50 text-black']: !error,
              },
            )}
          >
            {loading ? (
              <LoadingDots size={20} />
            ) : (
              <>
                <PlusCircleIcon className="h-5 w-5" aria-hidden="true" />
                <span className="ml-1.5 text-sm font-medium leading-5">
                  {chooseFileText}
                </span>
              </>
            )}

            <input
              id={name}
              name={name}
              type="file"
              className="sr-only"
              ref={ref}
              onChange={handleUpload}
              disabled={loading}
              {...rest}
            />
          </label>

          {url && (
            <Image
              className="ml-[10px] h-5 w-5 rounded-full"
              width={20}
              height={20}
              src={url}
              alt="file-preview"
            />
          )}

          <p className="mx-[10px] max-w-xs flex-1 truncate text-sm font-normal leading-5 text-gray-500">
            {loading
              ? uploadingText
              : fileName
                ? fileName
                : url || noFileChosenText}
          </p>

          {url && handleRemove && (
            <button
              type="button"
              onClick={handleRemove}
              className="ml-auto mr-1 flex h-5 w-5 items-center justify-center"
            >
              <XCircleIcon
                className="h-5 w-5 text-gray-500"
                aria-hidden="true"
              />
            </button>
          )}
        </div>
      </div>
    );
  },
);

ImageInput.displayName = 'ImageInput';

export default ImageInput;
